import type { IncidentReviewOutcome } from '../../services/incidentsApi';

export const PATTERN_CLOSURE_REASON_LABEL = 'Closure reason';
export const PATTERN_CLOSURE_REASON_PLACEHOLDER = 'Select a closure reason';
export const PATTERN_CLOSURE_REASON_HELPER =
  'Choose why these open linked records should leave the review queue. This reason is saved to the audit trail.';
export const PATTERN_CLOSURE_QUEUE_NOTE =
  'Closing removes these pattern observations from the open review queue. It does not prove the hazard was resolved or risk was reduced.';
export const PATTERN_CLOSURE_SELECT_BUTTON_LABEL = 'Select closure reason';

export type PatternClosureReasonOption = {
  value: IncidentReviewOutcome;
  label: string;
  explanation: string;
};

/** Bulk pattern close — honest at scale; must not claim per-row human verification. */
export const BULK_PATTERN_CLOSURE_REASON_OPTIONS: PatternClosureReasonOption[] = [
  {
    value: 'duplicate',
    label: 'Duplicate / already covered',
    explanation:
      'Use when these occurrences repeat an issue you already verified on representative evidence elsewhere in this pattern.',
  },
  {
    value: 'false_positive',
    label: 'False positive / detection issue',
    explanation:
      'Use when the detection or zone rule was wrong. Consider adjusting the policy after closing.',
  },
  {
    value: 'expected_activity',
    label: 'Expected activity / no action needed',
    explanation:
      'Use when the activity is normal for the site, such as authorized traffic or expected shift movement.',
  },
  {
    value: 'insufficient_evidence',
    label: 'Insufficient evidence / close as inconclusive',
    explanation:
      'Use when the evidence is not enough to act on, but you still want to clear the queue.',
  },
];

/** @deprecated Prefer BULK_PATTERN_CLOSURE_REASON_OPTIONS for bulk close UI. */
export const PATTERN_CLOSURE_REASON_OPTIONS: PatternClosureReasonOption[] = [
  ...BULK_PATTERN_CLOSURE_REASON_OPTIONS,
  {
    value: 'true_event',
    label: 'True risk reviewed / follow-up handled',
    explanation:
      'Use only on a single observation after representative verification — not for bulk close.',
  },
  {
    value: 'demo_or_test_activity',
    label: 'Special-case activity',
    explanation: 'Use this for replay, staged validation, smoke-test, or other nonstandard activity.',
  },
];

export function isBulkAllowedPatternClosureOutcome(
  outcome: IncidentReviewOutcome | '' | null | undefined,
): boolean {
  if (!outcome) return false;
  return BULK_PATTERN_CLOSURE_REASON_OPTIONS.some((option) => option.value === outcome);
}

export function getPatternClosureReasonExplanation(
  outcome: IncidentReviewOutcome | '' | null | undefined,
): string {
  if (!outcome) return '';
  return PATTERN_CLOSURE_REASON_OPTIONS.find((opt) => opt.value === outcome)?.explanation || '';
}

export function getPatternClosureReasonLabel(
  outcome: IncidentReviewOutcome | '' | null | undefined,
): string {
  if (!outcome) return PATTERN_CLOSURE_REASON_PLACEHOLDER;
  return PATTERN_CLOSURE_REASON_OPTIONS.find((opt) => opt.value === outcome)?.label || outcome;
}
